import {
  ChangeDetectionStrategy, Component, DestroyRef, ElementRef, EventEmitter, HostListener,
  Input, OnInit, Output, PLATFORM_ID, QueryList, ViewChild, ViewChildren, computed, inject, signal,
} from '@angular/core';
import { NgTemplateOutlet, isPlatformBrowser } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationStart, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';

import { AccountSummaryService } from '../../services/account-summary.service';

// ── Greeting ─────────────────────────────────────────────────────────────────

export type GreetingPeriod = 'morning' | 'noon' | 'evening' | 'night';

/**
 * Device-local hour -> period. Uses the browser clock (Date#getHours), never
 * the server's timezone.
 *   05:00-11:59 morning · 12:00-16:59 noon · 17:00-20:59 evening · 21:00-04:59 night
 */
export function greetingPeriodFor(date: Date): GreetingPeriod {
  const h = date.getHours();
  if (h >= 5 && h < 12) return 'morning';
  if (h >= 12 && h < 17) return 'noon';
  if (h >= 17 && h < 21) return 'evening';
  return 'night';
}

/** The next local time the period changes (05:00, 12:00, 17:00 or 21:00). */
export function nextGreetingBoundary(date: Date): Date {
  const boundaries = [5, 12, 17, 21];
  for (const h of boundaries) {
    const b = new Date(date.getFullYear(), date.getMonth(), date.getDate(), h, 0, 0, 0);
    if (b.getTime() > date.getTime()) return b;
  }
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1, 5, 0, 0, 0);
}

const GREETING_EMOJI: Record<GreetingPeriod, string> = {
  morning: '☀️',
  noon: '',
  evening: '🌇',
  night: '🌙',
};

/** Re-check at least this often, so a sleeping laptop or a timezone change self-corrects. */
const MAX_TIMER_MS = 15 * 60 * 1000;

interface MenuEntry {
  key: string;      // i18n key
  link: string;
  icon: string;     // inline SVG path data, 24x24, stroked
}

/** Only routes that exist today (app.routing.ts). No placeholders. */
const MENU: MenuEntry[] = [
  { key: 'sale4you.account.menu.account',      link: '/personal/user-info',
    icon: 'M12 12a4 4 0 100-8 4 4 0 000 8zM4.5 20a7.5 7.5 0 0115 0' },
  { key: 'sale4you.account.menu.myAds',        link: '/personal/my-ads',
    icon: 'M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8l-5-5zM14 3v5h5M9 13h6M9 17h6' },
  { key: 'sale4you.account.menu.messages',     link: '/personal/inquiries',
    icon: 'M4 5h16a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V6a1 1 0 011-1zM3.5 6.5L12 13l8.5-6.5' },
  { key: 'sale4you.account.menu.favorites',    link: '/personal/wishlist',
    icon: 'M20.8 5.6a5 5 0 00-7.1 0L12 7.3l-1.7-1.7a5 5 0 10-7.1 7.1L12 21l8.8-8.3a5 5 0 000-7.1z' },
  { key: 'sale4you.account.menu.benefits',     link: '/personal/benefits',
    icon: 'M4 7h16v3a2 2 0 000 4v3H4v-3a2 2 0 000-4V7zM14 7v10' },
  { key: 'sale4you.account.menu.subscription', link: '/personal/vip-subscription',
    icon: 'M3.5 8l4.5 4 4-6 4 6 4.5-4-2 10.5h-13L3.5 8z' },
];

/**
 * Header account chip (avatar · time-of-day greeting · VIP / regular badge)
 * with its dropdown menu.
 *
 *   variant="menu"    desktop / tablet header trigger + dropdown
 *   variant="summary" static card for the mobile drawer (no dropdown)
 *
 * Rendered only for signed-in users - the parent keeps the login button for
 * guests. Logout is emitted, not performed, so the header's single existing
 * logout() stays the only implementation.
 */
@Component({
  selector: 'app-account-menu',
  standalone: true,
  imports: [NgTemplateOutlet, RouterLink, RouterLinkActive, TranslateModule],
  templateUrl: './account-menu.component.html',
  styleUrls: ['./account-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountMenuComponent implements OnInit {
  @Input() variant: 'menu' | 'summary' = 'menu';
  @Output() logout = new EventEmitter<void>();
  /** Summary variant: lets the drawer close itself when a link is used. */
  @Output() navigated = new EventEmitter<void>();

  @ViewChild('trigger') private triggerRef?: ElementRef<HTMLButtonElement>;
  @ViewChildren('item') private items?: QueryList<ElementRef<HTMLElement>>;

  private readonly account = inject(AccountSummaryService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  readonly menu = MENU;
  readonly menuId = `account-menu-${Math.random().toString(36).slice(2, 8)}`;

  readonly state = toSignal(this.account.state$, { initialValue: this.account.snapshot });
  readonly open = signal(false);
  readonly avatarFailed = signal<string | null>(null);   // the URL that failed

  private readonly period = signal<GreetingPeriod>(greetingPeriodFor(new Date()));
  readonly greetingKey = computed(() => `sale4you.account.greeting.${this.period()}`);
  readonly greetingEmoji = computed(() => GREETING_EMOJI[this.period()]);

  readonly showImage = computed(() => {
    const url = this.state().avatarUrl;
    return !!url && this.avatarFailed() !== url;
  });

  /** Dropdown header title: full name, else email, else nothing. */
  readonly displayName = computed(() => this.state().fullName || this.state().email);

  private timer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.account.ensureLoaded();
    if (!this.isBrowser) {
      return;
    }
    this.scheduleGreeting();

    const onVisible = () => {
      if (document.visibilityState === 'visible') this.scheduleGreeting();
    };
    document.addEventListener('visibilitychange', onVisible);

    // Any navigation (menu item, back button, other link) closes the menu.
    const nav = this.router.events
      .pipe(filter(e => e instanceof NavigationStart))
      .subscribe(() => this.open.set(false));

    this.destroyRef.onDestroy(() => {
      if (this.timer) clearTimeout(this.timer);
      document.removeEventListener('visibilitychange', onVisible);
      nav.unsubscribe();
    });
  }

  // ── greeting timer ───────────────────────────────────────────────────────
  /** One timeout to the next boundary (capped), then re-arm. No polling loop. */
  private scheduleGreeting(): void {
    if (this.timer) clearTimeout(this.timer);
    const now = new Date();
    this.period.set(greetingPeriodFor(now));
    const wait = Math.min(nextGreetingBoundary(now).getTime() - now.getTime() + 1000, MAX_TIMER_MS);
    this.timer = setTimeout(() => this.scheduleGreeting(), Math.max(wait, 1000));
  }

  // ── avatar ───────────────────────────────────────────────────────────────
  onAvatarError(): void {
    this.avatarFailed.set(this.state().avatarUrl);
  }

  // ── dropdown ─────────────────────────────────────────────────────────────
  toggle(): void {
    this.open() ? this.close(false) : this.openMenu(0);
  }

  onTriggerKeydown(e: KeyboardEvent): void {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      this.openMenu(e.key === 'ArrowUp' ? -1 : 0);
    }
  }

  private openMenu(focusIndex: number): void {
    this.open.set(true);
    // Items render on the next change detection; focus them after it.
    setTimeout(() => this.focusItem(focusIndex));
  }

  close(returnFocus = true): void {
    if (!this.open()) return;
    this.open.set(false);
    if (returnFocus) this.triggerRef?.nativeElement.focus();
  }

  onMenuKeydown(e: KeyboardEvent): void {
    const list = this.items?.toArray() ?? [];
    if (!list.length) return;
    const current = list.findIndex(i => i.nativeElement === document.activeElement);
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); this.focusItem((current + 1) % list.length); break;
      case 'ArrowUp':   e.preventDefault(); this.focusItem((current - 1 + list.length) % list.length); break;
      case 'Home':      e.preventDefault(); this.focusItem(0); break;
      case 'End':       e.preventDefault(); this.focusItem(-1); break;
      case 'Escape':    e.preventDefault(); this.close(true); break;
      case 'Tab':       this.close(false); break;
    }
  }

  private focusItem(index: number): void {
    const list = this.items?.toArray() ?? [];
    if (!list.length) return;
    const i = index < 0 ? list.length - 1 : index;
    list[i]?.nativeElement.focus();
  }

  onItemClick(): void {
    this.open.set(false);
    this.navigated.emit();
  }

  onLogout(): void {
    this.open.set(false);
    this.logout.emit();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(e: MouseEvent): void {
    if (this.open() && !this.host.nativeElement.contains(e.target as Node)) {
      this.open.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close(true);
  }
}
